import React, { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Home,
  Search,
  Plus,
  Settings,
  BarChart3,
  FileText,
  Users,
  HelpCircle,
  Folder,
  Tag,
  Database,
  Mail,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Construction,
  ClipboardCheck,
  CheckSquare,
  } from 'lucide-react'
import { useUIStore } from '@/stores/ui'
import { supabase } from '@/lib/supabase'
import { cachedFetch, cacheKeys } from '@/lib/cache'
import { useKnowledgeRealtime, useImportSourcesRealtime } from '@/hooks/use-realtime'
import { PreferencesDialog } from '@/components/ui/preferences-dialog'

const navigation = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: Home
  },
  {
    name: 'Knowledge',
    href: '/knowledge',
    icon: FileText
  },
  {
    name: 'QA Review',
    href: '/admin/qa-review',
    icon: CheckSquare
  },
  {
    name: 'Tickets',
    href: '/ticket-system',
    icon: BookOpen
  }
]

// Email QA Tools - Separate group
const emailQANavigation = [
  {
    name: 'E-Mail QA',
    href: '/admin/email-qa',
    icon: ClipboardCheck
  },
  {
    name: 'QS Test Protokoll',
    href: '/admin/qa-test-protocol',
    icon: BarChart3
  }
]

interface TagWithCount {
  tag: string
  count: number
}

interface SourceWithCount {
  source: string
  displayName: string
  count: number
  icon: any
}

const adminNavigation = [
  {
    name: 'Import Quellen',
    href: '/admin/import-sources',
    icon: Folder
  },
  {
    name: 'Speicher',
    href: '/storage',
    icon: Database
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: BarChart3,
    disabled: true,
    constructionIcon: Construction
  },
  {
    name: 'Benutzer',
    href: '/admin/users',
    icon: Users,
    disabled: true,
    constructionIcon: Construction
  }
]

export function Sidebar() {
  const location = useLocation()
  const { sidebarOpen, toggleSidebar } = useUIStore()
  const [tags, setTags] = useState<TagWithCount[]>([])
  const [sources, setSources] = useState<SourceWithCount[]>([])
  const [loading, setLoading] = useState(true)
  const [sourcesLoading, setSourcesLoading] = useState(true)
  const [tagsExpanded, setTagsExpanded] = useState(false)
  const [emailToolsExpanded, setEmailToolsExpanded] = useState(true)

  // Close sidebar on mobile when navigating
  const handleNavClick = () => {
    if (window.innerWidth < 1024) { // lg breakpoint
      toggleSidebar()
    }
  }

  useEffect(() => {
    fetchTagsWithCounts()
    fetchSourcesWithCounts()
  }, [])

  // Subscribe to realtime updates
  useKnowledgeRealtime(() => {
    // Refetch tags when knowledge changes
    fetchTagsWithCounts()
  })

  useImportSourcesRealtime(() => {
    // Refetch sources when import sources change
    fetchSourcesWithCounts()
  })

  const fetchTagsWithCounts = async () => {
    try {
      const data = await cachedFetch(
        cacheKeys.tags(),
        async () => {
          const { data, error } = await supabase
            .from('knowledge')
            .select('tags')
            .not('tags', 'is', null)

          if (error) throw error
          return data
        },
        30000 // Cache for 30 seconds
      )

      // Process tags to count occurrences
      const tagCounts = new Map<string, number>()

      data?.forEach(item => {
        if (item.tags && Array.isArray(item.tags)) {
          item.tags.forEach((tag: string) => {
            if (tag) {
              tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1)
            }
          })
        }
      })

      // Convert to array and sort by count
      const tagsArray = Array.from(tagCounts.entries())
        .map(([tag, count]) => ({ tag, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10) // Show top 10 tags

      setTags(tagsArray)
    } catch (error) {
      console.error('Error fetching tags:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSourcesWithCounts = async () => {
    try {
      setSourcesLoading(true)

      // Clear cache for sources to get fresh data
      localStorage.removeItem(cacheKeys.sources())

      // Query to count knowledge entries per source type
      // Join knowledge with import_sources to get actual processable entries
      const { data: knowledgeCounts, error } = await supabase
        .from('knowledge')
        .select(`
          source_uid,
          import_sources!inner(source_type)
        `)

      if (error) throw error

      // Count knowledge entries by source type
      const sourceCounts = new Map<string, number>()

      knowledgeCounts?.forEach((item: any) => {
        const sourceType = item.import_sources?.source_type
        if (sourceType) {
          sourceCounts.set(sourceType, (sourceCounts.get(sourceType) || 0) + 1)
        }
      })

      console.log('Knowledge counts by source type:', Object.fromEntries(sourceCounts))

      // Map source types to display names and icons
      const sourceMapping = {
        'neoom': { name: 'Neoom Wiki', icon: BookOpen },
        'notion': { name: 'Notion Wiki', icon: FileText },
        'email': { name: 'Emails', icon: Mail },
        'manual': { name: 'Manuell', icon: FileText }
      }

      // Convert to array with display names, include all source types even with 0 count
      const allSourceTypes = Object.keys(sourceMapping)
      const sourcesArray = allSourceTypes.map(sourceType => ({
        source: sourceType,
        displayName: sourceMapping[sourceType as keyof typeof sourceMapping]?.name || sourceType,
        count: sourceCounts.get(sourceType) || 0,
        icon: sourceMapping[sourceType as keyof typeof sourceMapping]?.icon || Database
      }))
      // Sort by predefined order and filter out zero counts
      .filter(item => item.count > 0)
      .sort((a, b) => {
        const order = ['neoom', 'notion', 'email', 'manual']
        const aIndex = order.indexOf(a.source)
        const bIndex = order.indexOf(b.source)

        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex
        }
        if (aIndex !== -1) return -1
        if (bIndex !== -1) return 1
        return a.source.localeCompare(b.source)
      })

      setSources(sourcesArray)
    } catch (error) {
      console.error('Error fetching sources:', error)
    } finally {
      setSourcesLoading(false)
    }
  }

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => useUIStore.getState().toggleSidebar()}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
          'lg:w-64 md:w-60 sm:w-56', // Responsive widths
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <ScrollArea className="h-full py-6">
        <div className="px-3 space-y-6">
          {/* Main Navigation */}
          <nav className="space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      isActive
                        ? 'bg-missionSolar-primaryTeal text-white'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )
                  }
                >
                  <item.icon className="mr-3 h-4 w-4" />
                  {item.name}
                </NavLink>
              )
            })}
          </nav>

          <Separator />

          {/* Sources */}
          <div>
            <h3 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              📂 Quellen
            </h3>
            <nav className="space-y-1">
              {sourcesLoading ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  Lade Quellen...
                </div>
              ) : sources.length > 0 ? (
                sources.map((source) => (
                  <NavLink
                    key={source.source}
                    to={`/knowledge?source=${source.source}`}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors',
                        isActive
                          ? 'bg-missionSolar-primaryTeal text-white'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <div className="flex items-center">
                          <source.icon className="mr-3 h-4 w-4" />
                          {source.displayName}
                        </div>
                        <span className={cn(
                          "text-xs px-2 py-1 rounded-full",
                          isActive
                            ? "bg-white/20 text-white"
                            : "bg-muted"
                        )}>
                          {source.count}
                        </span>
                      </>
                    )}
                  </NavLink>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  Keine Quellen vorhanden
                </div>
              )}
            </nav>
          </div>

          <Separator />

          {/* Tags */}
          <div>
            <button
              onClick={() => setTagsExpanded(!tagsExpanded)}
              className="w-full flex items-center justify-between px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
            >
              <span className="flex items-center">
                {tagsExpanded ? (
                  <ChevronDown className="mr-1 h-3 w-3" />
                ) : (
                  <ChevronRight className="mr-1 h-3 w-3" />
                )}
                🏷️ Tags (Themen)
              </span>
              {!tagsExpanded && tags.length > 0 && (
                <span className="text-xs bg-muted px-2 py-0.5 rounded-full">
                  {tags.length}
                </span>
              )}
            </button>
            {tagsExpanded && (
              <nav className="space-y-1">
                {loading ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Lade Tags...
                  </div>
                ) : tags.length > 0 ? (
                  tags.map((tag) => (
                    <NavLink
                      key={tag.tag}
                      to={`/knowledge?tag=${encodeURIComponent(tag.tag)}`}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors',
                          isActive
                            ? 'bg-missionSolar-primaryTeal text-white'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <div className="flex items-center">
                            <Tag className="mr-3 h-4 w-4" />
                            {tag.tag}
                          </div>
                          <span className={cn(
                            "text-xs px-2 py-1 rounded-full",
                            isActive
                              ? "bg-white/20 text-white"
                              : "bg-muted"
                          )}>
                            {tag.count}
                          </span>
                        </>
                      )}
                    </NavLink>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Keine Tags vorhanden
                  </div>
                )}
              </nav>
            )}
          </div>

          <Separator />

          {/* Email QA Tools */}
          <div>
            <button
              onClick={() => setEmailToolsExpanded(!emailToolsExpanded)}
              className="w-full flex items-center justify-between px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
            >
              <span className="flex items-center">
                {emailToolsExpanded ? (
                  <ChevronDown className="mr-1 h-3 w-3" />
                ) : (
                  <ChevronRight className="mr-1 h-3 w-3" />
                )}
                📧 E-Mail QA Tools
              </span>
              <span className="text-xs bg-muted px-2 py-0.5 rounded-full">
                {emailQANavigation.length}
              </span>
            </button>
            {emailToolsExpanded && (
              <nav className="space-y-1">
                {emailQANavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={handleNavClick}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                        isActive
                          ? 'bg-missionSolar-primaryTeal text-white'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      )
                    }
                  >
                    <item.icon className="mr-3 h-4 w-4" />
                    {item.name}
                  </NavLink>
                ))}
              </nav>
            )}
          </div>

          <Separator />

          {/* Admin Navigation */}
          <div>
            <h3 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Administration
            </h3>
            <nav className="space-y-1">
              {adminNavigation.map((item) => {
                const isDisabled = item.disabled
                const Component = isDisabled ? 'div' : NavLink
                const props = isDisabled
                  ? {
                      className: 'flex items-center px-3 py-2 text-sm font-medium rounded-md text-muted-foreground/50 cursor-not-allowed'
                    }
                  : {
                      to: item.href,
                      onClick: handleNavClick,
                      className: ({ isActive }: { isActive: boolean }) =>
                        cn(
                          'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                          isActive
                            ? 'bg-missionSolar-primaryTeal text-white'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        )
                    }

                return (
                  <Component key={item.name} {...(props as any)}>
                    <item.icon className="mr-3 h-4 w-4" />
                    {item.name}
                    {item.constructionIcon && (
                      <item.constructionIcon className="ml-auto h-3.5 w-3.5 text-muted-foreground/40" />
                    )}
                  </Component>
                )
              })}
            </nav>
          </div>

          <Separator />

          {/* Preferences & Help */}
          <div className="space-y-1">
            <PreferencesDialog>
              <Button
                variant="ghost"
                className="w-full justify-start text-muted-foreground hover:text-foreground"
              >
                <Settings className="mr-3 h-4 w-4" />
                Einstellungen
              </Button>
            </PreferencesDialog>

            <NavLink
              to="/api-docs"
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  isActive
                    ? 'bg-missionSolar-primaryTeal text-white'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )
              }
            >
              <HelpCircle className="mr-3 h-4 w-4" />
              API Dokumentation
            </NavLink>

            </div>
        </div>
      </ScrollArea>
    </div>
    </>
  )
}